import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase, supabaseConfigured } from "../lib/supabase.js";

const AuthContext = createContext(null);

const STORAGE_KEY = "lc_user";

function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

function loadLocalUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function supabaseUserToLocal(u) {
  if (!u) return null;
  const meta = u.user_metadata || {};
  const providers = u.app_metadata?.providers || [u.app_metadata?.provider].filter(Boolean);
  return {
    provider: providers[0] || "email",
    id: u.id,
    name: meta.full_name || meta.name || (u.email || "").split("@")[0] || "Learner",
    email: u.email || "",
    picture: meta.avatar_url || meta.picture || "",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => loadLocalUser());
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(supabaseConfigured);

  // When Supabase is configured, let it own the session.
  useEffect(() => {
    if (!supabase) return;
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setUser(supabaseUserToLocal(data.session?.user) || loadLocalUser());
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(supabaseUserToLocal(s?.user) || null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Mirror user → localStorage so the old guest flow keeps working.
  useEffect(() => {
    if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY);
  }, [user]);

  // OAuth — Supabase handles the popup/redirect dance.
  const signInWithOAuth = async (provider) => {
    if (!supabase) {
      // Demo fallback when Supabase isn't configured.
      setUser({
        provider,
        id: `demo-${provider}-${Date.now()}`,
        name: `Demo ${provider[0].toUpperCase() + provider.slice(1)} User`,
        email: `demo@${provider}.local`,
        picture: "",
      });
      return { ok: true, demo: true };
    }
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true, url: data?.url };
  };

  // Legacy Google ID-token path (kept so @react-oauth/google GoogleLogin keeps working
  // if someone wires it up). Supabase OAuth is preferred when configured.
  const signInWithGoogleCredential = (credential) => {
    if (supabase) {
      return supabase.auth
        .signInWithIdToken({ provider: "google", token: credential })
        .then(({ error }) => {
          if (error) throw new Error(error.message);
        });
    }
    const claims = decodeJwt(credential) || {};
    setUser({
      provider: "google",
      id: claims.sub || claims.email,
      name: claims.name || "Learner",
      email: claims.email || "",
      picture: claims.picture || "",
    });
  };

  const signInWithProvider = (provider, profile) => {
    // Demo path only — real OAuth goes through signInWithOAuth.
    setUser({
      provider,
      id: profile.id || profile.email,
      name: profile.name || "Learner",
      email: profile.email || "",
      picture: profile.picture || "",
    });
  };

  const signInAsGuest = (name = "Guest Learner") => {
    setUser({
      provider: "guest",
      id: `guest-${Date.now()}`,
      name,
      email: "",
      picture: "",
    });
  };

  const signOut = () => {
    setUser(null);
    setSession(null);
    localStorage.removeItem("lc_screen");
    // Fire-and-forget on Supabase; onAuthStateChange listener will reconcile anyway.
    if (supabase) return supabase.auth.signOut();
  };

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      isAuthenticated: Boolean(user),
      supabaseConfigured,
      signInWithOAuth,
      signInWithGoogleCredential,
      signInWithProvider,
      signInAsGuest,
      signOut,
    }),
    [user, session, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
